<script setup lang="ts">
// 守狸 Web 完整端工作台（/ 路由，requiresAuth）：身份层（SSO）之后的 vault 主体——
// 新建/解锁云端保险箱 → 完整管理（账号/密钥 CRUD、强密码生成、风险巡检、账户安全）。
// 身份登录已上移到 /login + /callback（P8 SSO）；本页只管「拿到 token 之后」的 vault 链路，
// 与 P5/P6 完全一致（编辑安全回写云端 OCC 三路合并，明文/VDK 只在内存，锁定即清）。
import { computed, onMounted, onBeforeUnmount, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  session, initAfterAuth, bootstrap, unlock, lock, logout,
  upsertAccount, upsertSecret, deleteEntry, revealField,
  type AccountDraft, type SecretDraft,
} from '../vault-session'
import { sdk, isAuthed } from '../lib/sdk'
import { detectAccountRisks, detectSecretRisks } from '../lib/risk'
import type { AccountItem, SecretItem, Category, Project } from '../lib/model'
import EntryEditor from '../components/EntryEditor.vue'
import Cashier from '../components/Cashier.vue'
import AccountSecurity from '../components/AccountSecurity.vue'
import KeytailMascot from '../components/KeytailMascot.vue'

const router = useRouter()

// 身份层已就绪（main.ts bootstrap 或 /callback 换 token），但 vault 侧仍是初始 'login' 态 →
// 在此触发一次产品侧初始化（登记设备 / 读账户会员 / 探测 vault → setup 或 unlock）。
onMounted(() => {
  document.addEventListener('visibilitychange', onHide)
  for (const ev of idleEvents) window.addEventListener(ev, onActivity, { passive: true })
  if (isAuthed() && session.stage === 'login') initAfterAuth()
})

// 新建 / 解锁
const pin = ref('')
const pin2 = ref('')
const setupErr = ref('')
function onBootstrap() {
  setupErr.value = ''
  if (pin.value.length < 8) return (setupErr.value = '主密码至少 8 位')
  if (pin.value !== pin2.value) return (setupErr.value = '两次主密码不一致')
  bootstrap(pin.value).then(() => { pin.value = ''; pin2.value = '' })
}
function onUnlock() {
  if (pin.value) unlock(pin.value).then(() => (pin.value = ''))
}

// 退出：先吊销 account 会话（SDK CSRF 双提交 → /session/logout）+ 清 SDK 内存 token，
// 再清本地 vault 态，最后回品牌登录页。
async function onLogout() {
  await sdk.logout().catch(() => undefined)
  logout()
  router.replace('/login')
}

// 工作区
type Tab = 'accounts' | 'secrets' | 'risks'
const tab = ref<Tab>('accounts')
const query = ref('')
const accounts = computed<AccountItem[]>(() => (session.payload.accounts ?? []) as unknown as AccountItem[])
const secrets = computed<SecretItem[]>(() => (session.payload.secrets ?? []) as unknown as SecretItem[])
const categories = computed<Category[]>(() => (session.payload.categories ?? []) as unknown as Category[])
const projects = computed<Project[]>(() => (session.payload.projects ?? []) as unknown as Project[])
const catName = (id: string | null) => categories.value.find((c) => c.id === id)?.name ?? ''

const filteredAccounts = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return accounts.value
  return accounts.value.filter((a) =>
    [a.platform_name, a.username, a.email, a.phone].some((f) => f?.toLowerCase().includes(q)))
})
const filteredSecrets = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return secrets.value
  return secrets.value.filter((s) =>
    [s.name, s.platform_name, s.kind, s.key_id].some((f) => f?.toLowerCase().includes(q)))
})

const risks = computed(() => {
  const out: { id: string; level: string; message: string; target: string }[] = []
  for (const a of accounts.value) for (const r of detectAccountRisks(a)) out.push({ id: r.id, level: r.level, message: r.message, target: a.platform_name })
  for (const s of secrets.value) for (const r of detectSecretRisks(s)) out.push({ id: r.id, level: r.level, message: r.message, target: s.name })
  return out
})

// reveal / copy
const revealed = reactive<Record<string, string>>({})
const copiedId = ref('')
let copyTimer = 0
function toggleReveal(id: string, ct: string | null | undefined) {
  if (revealed[id]) { delete revealed[id]; return }
  try {
    revealed[id] = revealField(ct)
    window.setTimeout(() => delete revealed[id], 10000)
  } catch { session.notice = '解密失败' }
}
async function copy(id: string, ct: string | null | undefined) {
  try {
    const v = revealField(ct)
    await navigator.clipboard.writeText(v)
    copiedId.value = id
    if (copyTimer) window.clearTimeout(copyTimer)
    copyTimer = window.setTimeout(() => (copiedId.value = ''), 1600)
    window.setTimeout(() => navigator.clipboard.writeText('').catch(() => undefined), 30000)
  } catch {
    toggleReveal(id, ct)
    session.notice = '复制被拒，请手动选中上方明文复制'
    window.setTimeout(() => (session.notice = ''), 4000)
  }
}

// 编辑器
const editor = ref<{ kind: 'account' | 'secret'; entry: AccountItem | SecretItem | null } | null>(null)
function openCreate(kind: 'account' | 'secret') { editor.value = { kind, entry: null } }
function openEdit(kind: 'account' | 'secret', entry: AccountItem | SecretItem) { editor.value = { kind, entry } }
async function onSaveAccount(d: AccountDraft) { editor.value = null; await upsertAccount(d).catch(() => undefined) }
async function onSaveSecret(d: SecretDraft) { editor.value = null; await upsertSecret(d).catch(() => undefined) }
async function onDelete(kind: 'account' | 'secret', id: string, label: string) {
  if (!confirm(`确定删除「${label}」？此操作会同步到云端。`)) return
  await deleteEntry(kind, id).catch(() => undefined)
}

const cashier = ref<'membership' | null>(null)
const securityOpen = ref(false)
const isMember = computed(() => !!(session.membership?.cloudEnabled || session.account?.member))
const syncLabel = computed(() => ({ idle: '', syncing: '同步中…', synced: '已同步', conflict: '已自动合并冲突', error: '同步失败' }[session.syncState]))

// 内存卫生：空闲自动锁 + 切走清明文。
const IDLE_MS = 180_000
let idleTimer = 0
function armIdle() { if (idleTimer) window.clearTimeout(idleTimer); idleTimer = window.setTimeout(() => { if (session.stage === 'workspace') lock() }, IDLE_MS) }
function onActivity() { if (session.stage === 'workspace') armIdle() }
function onHide() {
  if (document.visibilityState === 'hidden') {
    for (const k of Object.keys(revealed)) delete revealed[k]
    if (editor.value) editor.value = null // 切走清编辑表单明文
  }
}
const idleEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onHide)
  for (const ev of idleEvents) window.removeEventListener(ev, onActivity)
  if (idleTimer) window.clearTimeout(idleTimer)
})
</script>

<template>
  <div class="kt-app">
    <header class="kt-top">
      <div class="kt-brand"><KeytailMascot :size="26" class="kt-key" /> 灵澜守狸</div>
      <div v-if="session.account" class="kt-top__right">
        <span v-if="session.membership" class="kt-plan" :class="{ 'kt-plan--member': isMember }">{{ session.membership.planName }}</span>
        <button v-if="session.account && !isMember" class="kt-btn kt-btn--gold kt-btn--sm" @click="cashier = 'membership'">开通守狸云</button>
        <button v-if="session.stage === 'workspace'" class="kt-btn kt-btn--ghost kt-btn--sm" @click="securityOpen = true">账户安全</button>
        <button v-if="session.stage === 'workspace'" class="kt-btn kt-btn--ghost kt-btn--sm" @click="lock()">锁定</button>
        <button class="kt-btn kt-btn--ghost kt-btn--sm" @click="onLogout()">退出</button>
      </div>
    </header>

    <!-- 初始化中（身份已就绪，正在读账户/探测 vault）-->
    <section v-if="session.stage === 'login'" class="kt-card" style="max-width:440px;margin:0 auto;text-align:center">
      <p class="kt-progress" style="justify-content:center"><span class="kt-dot" /> {{ session.busyStep || '正在进入守狸云…' }}</p>
      <p v-if="session.error" class="kt-error" role="alert">{{ session.error }}</p>
    </section>

    <!-- 新建保险箱 -->
    <section v-else-if="session.stage === 'setup'" class="kt-card" style="max-width:440px;margin:0 auto">
      <h1>设置主密码，新建云端保险箱</h1>
      <p class="kt-hint">主密码（PIN）用于本地加密你的所有密钥——云端和我们都看不到。请牢记，并配合 S_server 才能解锁。至少 8 位。</p>
      <label class="kt-label">主密码</label>
      <input class="kt-input" v-model="pin" type="password" autocomplete="new-password" :disabled="session.busy" />
      <label class="kt-label">确认主密码</label>
      <input class="kt-input" v-model="pin2" type="password" autocomplete="new-password" :disabled="session.busy" @keydown.enter="onBootstrap" />
      <p v-if="setupErr || session.error" class="kt-error" role="alert">{{ setupErr || session.error }}</p>
      <p v-if="session.busy && session.busyStep" class="kt-progress"><span class="kt-dot" /> {{ session.busyStep }}</p>
      <button class="kt-btn kt-btn--primary" :disabled="session.busy" @click="onBootstrap">{{ session.busy ? '创建中…' : '创建保险箱' }}</button>
    </section>

    <!-- 解锁 -->
    <section v-else-if="session.stage === 'unlock'" class="kt-card" style="max-width:440px;margin:0 auto">
      <h1>输入主密码解锁</h1>
      <p class="kt-hint">用主密码解密云端保险箱。明文只在本地内存，关闭即清。</p>
      <label class="kt-label">主密码</label>
      <input class="kt-input" v-model="pin" type="password" autocomplete="current-password" :disabled="session.busy" @keydown.enter="onUnlock" />
      <p v-if="session.error" class="kt-error" role="alert">{{ session.error }}</p>
      <p v-if="session.busy && session.busyStep" class="kt-progress"><span class="kt-dot" /> {{ session.busyStep }}</p>
      <button class="kt-btn kt-btn--primary" :disabled="session.busy" @click="onUnlock">{{ session.busy ? '解锁中…' : '解锁' }}</button>
    </section>

    <!-- 工作区 -->
    <section v-else class="kt-workspace">
      <div class="kt-ws-bar">
        <nav class="kt-tabs">
          <button :class="{ on: tab === 'accounts' }" @click="tab = 'accounts'">账号 {{ accounts.length }}</button>
          <button :class="{ on: tab === 'secrets' }" @click="tab = 'secrets'">密钥 {{ secrets.length }}</button>
          <button :class="{ on: tab === 'risks' }" @click="tab = 'risks'">巡检 <span v-if="risks.length" class="kt-pill">{{ risks.length }}</span></button>
        </nav>
        <span class="kt-sync" :class="`kt-sync--${session.syncState}`">{{ syncLabel }}</span>
      </div>

      <div v-if="tab !== 'risks'" class="kt-ws-actions">
        <input class="kt-input kt-search" v-model="query" placeholder="搜索…" />
        <button class="kt-btn kt-btn--primary kt-btn--sm kt-new" @click="openCreate(tab === 'accounts' ? 'account' : 'secret')">
          + 新建{{ tab === 'accounts' ? '账号' : '密钥' }}
        </button>
      </div>

      <p v-if="session.notice" class="kt-notice">{{ session.notice }}</p>
      <p v-if="session.error" class="kt-error" role="alert">{{ session.error }}</p>

      <!-- 账号列表 -->
      <ul v-if="tab === 'accounts'" class="kt-list">
        <li v-for="a in filteredAccounts" :key="a.id" class="kt-item">
          <div class="kt-item__main">
            <div class="kt-item__title">
              {{ a.platform_name }}
              <span v-if="a.favorite" class="kt-star">★</span>
              <a v-if="a.platform_url" :href="a.platform_url" target="_blank" rel="noopener" class="kt-item__go">↗</a>
              <span v-if="catName(a.category_id)" class="kt-tagchip">{{ catName(a.category_id) }}</span>
            </div>
            <div class="kt-item__sub">{{ a.username || a.email || a.phone || '—' }}<span v-if="!a.has_2fa" class="kt-warn"> · 未开 2FA</span></div>
            <div v-if="a.password_ciphertext" class="kt-item__value kt-mono" :class="{ 'kt-item__value--revealed': revealed[a.id] }">{{ revealed[a.id] ?? '••••••••••••' }}</div>
          </div>
          <div class="kt-item__actions">
            <button v-if="a.password_ciphertext" class="kt-btn kt-btn--ghost kt-btn--sm" @click="toggleReveal(a.id, a.password_ciphertext)">{{ revealed[a.id] ? '隐藏' : '查看' }}</button>
            <button v-if="a.password_ciphertext" class="kt-btn kt-btn--ghost kt-btn--sm" :class="{ 'kt-btn--ok': copiedId === a.id }" @click="copy(a.id, a.password_ciphertext)">{{ copiedId === a.id ? '已复制' : '复制' }}</button>
            <button class="kt-btn kt-btn--ghost kt-btn--sm" @click="openEdit('account', a)">编辑</button>
            <button class="kt-btn kt-btn--ghost kt-btn--sm kt-del" @click="onDelete('account', a.id, a.platform_name)">删除</button>
          </div>
        </li>
        <li v-if="!filteredAccounts.length" class="kt-empty">还没有账号，点「新建账号」开始。</li>
      </ul>

      <!-- 密钥列表 -->
      <ul v-else-if="tab === 'secrets'" class="kt-list">
        <li v-for="s in filteredSecrets" :key="s.id" class="kt-item">
          <div class="kt-item__main">
            <div class="kt-item__title">{{ s.name }}<span class="kt-tagchip">{{ s.kind }}</span><span v-if="s.environment" class="kt-tagchip kt-tagchip--env">{{ s.environment }}</span></div>
            <div class="kt-item__sub">{{ [s.platform_name, s.key_id].filter(Boolean).join(' · ') || '—' }}</div>
            <div class="kt-item__value kt-mono" :class="{ 'kt-item__value--revealed': revealed[s.id] }">{{ revealed[s.id] ?? '••••••••••••' }}</div>
          </div>
          <div class="kt-item__actions">
            <button class="kt-btn kt-btn--ghost kt-btn--sm" @click="toggleReveal(s.id, s.secret_ciphertext)">{{ revealed[s.id] ? '隐藏' : '查看' }}</button>
            <button class="kt-btn kt-btn--ghost kt-btn--sm" :class="{ 'kt-btn--ok': copiedId === s.id }" @click="copy(s.id, s.secret_ciphertext)">{{ copiedId === s.id ? '已复制' : '复制' }}</button>
            <button class="kt-btn kt-btn--ghost kt-btn--sm" @click="openEdit('secret', s)">编辑</button>
            <button class="kt-btn kt-btn--ghost kt-btn--sm kt-del" @click="onDelete('secret', s.id, s.name)">删除</button>
          </div>
        </li>
        <li v-if="!filteredSecrets.length" class="kt-empty">还没有密钥，点「新建密钥」开始。</li>
      </ul>

      <!-- 巡检 -->
      <ul v-else class="kt-list">
        <li v-for="r in risks" :key="r.id" class="kt-item kt-risk" :data-level="r.level">
          <div class="kt-item__main">
            <div class="kt-item__title">{{ r.target }}</div>
            <div class="kt-item__sub">{{ r.message }}</div>
          </div>
        </li>
        <li v-if="!risks.length" class="kt-empty">一切井井有条，没有需要整理的项。</li>
      </ul>
    </section>

    <!-- 恢复码（bootstrap 一次性）-->
    <div v-if="session.lastRecoveryCode" class="kt-modal" role="dialog" aria-modal="true">
      <div class="kt-modal__panel">
        <header class="kt-modal__head"><h2>请抄下你的恢复码</h2></header>
        <p class="kt-hint">这串恢复码只显示这一次。请抄在纸上妥善保管。注意：网页端创建的保险箱，短信重置 PIN 需在桌面端补登记一次。</p>
        <p class="kt-recovery kt-mono">{{ session.lastRecoveryCode }}</p>
        <div class="kt-modal__foot"><button class="kt-btn kt-btn--primary" @click="session.lastRecoveryCode = ''">我已抄好</button></div>
      </div>
    </div>

    <EntryEditor v-if="editor" :kind="editor.kind" :entry="editor.entry" :categories="categories" :projects="projects"
      @save-account="onSaveAccount" @save-secret="onSaveSecret" @close="editor = null" />
    <Cashier v-if="cashier" :mode="cashier" @paid="cashier = null" @close="cashier = null" />
    <AccountSecurity v-if="securityOpen" @close="securityOpen = false" />
  </div>
</template>
